import { useMemo, useState } from "react";
import { NJIconButton } from "@engie-group/fluid-design-system-react";
import {
  Size2MixAccordion,
  Size2MixExpandCollapseBar,
  Size2MixSection,
  Size2MixSimpleTable,
  useSectionsToggle,
} from "@shared/size2mix-shell";
import {
  Size2MixParameterBlock,
  Size2MixParameterNumber,
  Size2MixParameterRow,
} from "@shared/size2mix-shell/Size2MixParameterBlock";
import temperature from "@shared/size2mix-shell/assets/temperature.svg?url";
import pressure from "@shared/size2mix-shell/assets/pressure.svg?url";
import substation from "@shared/size2mix-shell/assets/substation.svg?url";
import speed from "@shared/size2mix-shell/assets/speed.svg?url";
import type { TopologyParameters } from "./types";
import {
  ConfigurePipeCatalogModal,
  DeletePipeCatalogModal,
  EditPipeCatalogModal,
  PIPE_CATALOG_TABLE_COLUMNS,
  buildReferenceCatalogs,
  downloadPipeCatalog,
  type PipeCatalog,
} from "./PipeCatalogModals";

const SECTIONS = ["operating", "profiles", "sizing"] as const;

export default function ParametersTab({
  parameters: p,
}: {
  parameters: TopologyParameters;
}) {
  const { bind, expandAll, collapseAll } = useSectionsToggle(SECTIONS);
  const [catalog, setCatalog] = useState<PipeCatalog | null>(p.pipeCatalog);
  const [modal, setModal] = useState<"edit" | "configure" | "delete" | null>(
    null,
  );
  const references = useMemo(
    () => buildReferenceCatalogs(p.pipeCatalog),
    [p.pipeCatalog],
  );
  return (
    <Size2MixAccordion>
      <Size2MixExpandCollapseBar
        onExpandAll={expandAll}
        onCollapseAll={collapseAll}
      />
      <Size2MixSection
        title="Caractéristiques de fonctionnement"
        {...bind("operating")}
      >
        <div>
          <Size2MixParameterRow>
            <Size2MixParameterBlock
              title="Température (°C)"
              image={temperature}
              minHeight={144}
              footer={
                <Size2MixParameterNumber
                  label="Température d'ambiance (°C)"
                  value={p.temperature.ambient}
                />
              }
            >
              <Size2MixParameterNumber
                label="Réseau max"
                value={p.temperature.networkMax}
              />
              <Size2MixParameterNumber
                label="Injection max"
                value={p.temperature.injectionMax}
              />
              <Size2MixParameterNumber
                label="Injection min"
                value={p.temperature.injectionMin}
              />
              <Size2MixParameterNumber
                label="Réseau min"
                value={p.temperature.networkMin}
              />
            </Size2MixParameterBlock>
            <Size2MixParameterBlock
              title="Pression (bar)"
              image={pressure}
              minHeight={144}
              footer={
                <Size2MixParameterNumber
                  label="Taux de pertes de charges singulières"
                  value={p.pressure.singularLossRate}
                />
              }
            >
              <Size2MixParameterNumber label="Max" value={p.pressure.max} />
              <Size2MixParameterNumber label="Min" value={p.pressure.min} />
            </Size2MixParameterBlock>
            <Size2MixParameterBlock
              title="Sous-station (bar | Δ°C)"
              image={substation}
              minHeight={144}
            >
              <Size2MixParameterNumber
                label="DeltaP nominal"
                value={p.substation.deltaPNominal}
              />
              <Size2MixParameterNumber
                label="DeltaT nominal"
                value={p.substation.deltaTNominal}
              />
            </Size2MixParameterBlock>
          </Size2MixParameterRow>
        </div>
      </Size2MixSection>
      <Size2MixSection title="Profils simples" {...bind("profiles")}>
        <Size2MixParameterRow justifyBetween>
          <Size2MixParameterBlock
            title="Température extérieure (°C)"
            image={temperature}
          >
            <Size2MixParameterNumber label="Max" value={p.outdoorTemperature.max} />
            <Size2MixParameterNumber
              label="Inter"
              value={p.outdoorTemperature.inter}
            />
            <Size2MixParameterNumber label="Min" value={p.outdoorTemperature.min} />
          </Size2MixParameterBlock>
        </Size2MixParameterRow>
      </Size2MixSection>
      <Size2MixSection title="Critères de dimensionnement" {...bind("sizing")}>
        <Size2MixParameterRow>
          <Size2MixParameterBlock
            title="Vitesse maximale dans les canalisations (m/s)"
            subtitle="appliqué uniquement aux canalisations redimensionnables"
            image={speed}
          >
            <Size2MixParameterNumber label="Max" value={p.sizing.maxVelocity} />
          </Size2MixParameterBlock>
          <Size2MixParameterBlock
            title="Gradient de pression maximal (mmCE/m)"
            subtitle="appliqué uniquement aux canalisations redimensionnables"
            image={pressure}
          >
            <Size2MixParameterNumber
              label="Max"
              value={p.sizing.maxPressureGradient}
            />
          </Size2MixParameterBlock>
        </Size2MixParameterRow>
        <div style={{ width: "100%", marginTop: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <p className="nj-text nj-text--md" style={{ margin: 0 }}>
                Catalogue de canalisations :{" "}
                <em>{catalog ? catalog.name : "aucun"}</em>
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {catalog && (
                  <>
                    <NJIconButton
                      icon="edit"
                      label=""
                      aria-label="Modifier"
                      variant="tertiary"
                      onClick={() => setModal("edit")}
                    />
                    <NJIconButton
                      icon="download"
                      label=""
                      aria-label="Télécharger"
                      variant="tertiary"
                      onClick={() => downloadPipeCatalog(catalog)}
                    />
                  </>
                )}
                <NJIconButton
                  icon="description"
                  label=""
                  aria-label="Remplacer le catalogue"
                  variant="tertiary"
                  onClick={() => setModal("configure")}
                />
                {catalog && (
                  <NJIconButton
                    icon="delete_outline"
                    label=""
                    aria-label="Supprimer"
                    variant="tertiary"
                    onClick={() => setModal("delete")}
                  />
                )}
              </div>
            </div>
            {catalog && (
              <div
                className="mt-4"
                data-testid="pipe-catalog-table"
                style={{ marginTop: 16 }}
              >
                <Size2MixSimpleTable
                  rowHeight={30}
                  headerHeight={54}
                  wrapHeaderText={false}
                  columns={PIPE_CATALOG_TABLE_COLUMNS}
                  rows={catalog.rows}
                  rowKey={(row) => String(row.dn)}
                />
              </div>
            )}
          </div>
        </div>
        {modal === "edit" && catalog && (
          <EditPipeCatalogModal
            catalog={catalog}
            onClose={() => setModal(null)}
            onValidate={(next) => {
              setCatalog(next);
              setModal(null);
            }}
          />
        )}
        {modal === "configure" && (
          <ConfigurePipeCatalogModal
            references={references}
            onClose={() => setModal(null)}
            onValidate={(next) => {
              setCatalog(next);
              setModal(null);
            }}
          />
        )}
        {modal === "delete" && catalog && (
          <DeletePipeCatalogModal
            catalogName={catalog.name}
            onClose={() => setModal(null)}
            onConfirm={() => {
              setCatalog(null);
              setModal(null);
            }}
          />
        )}
      </Size2MixSection>
    </Size2MixAccordion>
  );
}
